import React, { useEffect, useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useLogbook } from '../../contexts/LogbookContext';
import LogTheme from './LogTheme';
import { Typography, Box, Card, CardContent, Button, Grid, IconButton, Tooltip, TextField, InputAdornment, Select, MenuItem, FormControl, InputLabel, CircularProgress, Skeleton, Chip, Divider } from '@mui/material';
import ViewModuleIcon from '@mui/icons-material/ViewModule';
import ViewListIcon from '@mui/icons-material/ViewList';
import SearchIcon from '@mui/icons-material/Search';
import { getAuthHeaders, getImageDisplayUrl } from '../../utils/api';

function Blog() {
  const [logs, setLogs] = useState([]);
  const [isUserIdLoaded, setIsUserIdLoaded] = useState(false);
  const [sortOrder, setSortOrder] = useState('newest');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');
  const [yearFilter, setYearFilter] = useState('');
  const [loading, setLoading] = useState(true);

  const { userId, forceUpdate } = useAuth();
  const { currentLogbookId } = useLogbook();

  useEffect(() => {
    if (userId) {
      setIsUserIdLoaded(true);
      setLoading(true);
      const url = `${process.env.REACT_APP_SERVER_URL}/api/logs${currentLogbookId ? `?logbookId=${encodeURIComponent(currentLogbookId)}` : ''}`;
      fetch(url, {
        method: 'GET',
        headers: getAuthHeaders(),
      })
        .then((response) => {
          if (!response.ok) {
            if (response.status === 401) {
              console.error('401: Inte autentiserad. Logga in igen.');
              return [];
            }
            throw new Error(`HTTP error! status: ${response.status}`);
          }
          return response.json();
        })
        .then((data) => {
          if (!data || !Array.isArray(data)) {
            setLogs([]);
            return;
          }
          const sortedLogs = data.sort((a, b) => {
            if (sortOrder === 'newest') {
              return new Date(b.departureDateTime) - new Date(a.departureDateTime);
            } else {
              return new Date(a.departureDateTime) - new Date(b.departureDateTime);
            }
          });
          setLogs(sortedLogs);
        })
        .catch((error) => {
          console.error('Error fetching logs:', error);
          setLogs([]);
        })
        .finally(() => setLoading(false));
    } else {
      setLogs([]);
      setLoading(false);
    }
  }, [userId, forceUpdate, sortOrder, currentLogbookId]);

  // Get unique years from logs
  const availableYears = useMemo(() => {
    const years = logs
      .map(log => log.year)
      .filter(year => year != null)
      .map(year => parseInt(year))
      .filter((year, index, self) => self.indexOf(year) === index)
      .sort((a, b) => b - a);
    return years;
  }, [logs]);

  // Filter and search logs
  const filteredLogs = useMemo(() => {
    let filtered = [...logs];

    // Year filter
    if (yearFilter) {
      filtered = filtered.filter(log => log.year && parseInt(log.year) === parseInt(yearFilter));
    }

    // Search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(log => 
        (log.title && log.title.toLowerCase().includes(query)) ||
        (log.description && log.description.toLowerCase().includes(query)) ||
        (log.position && log.position.toLowerCase().includes(query))
      );
    }

    return filtered;
  }, [logs, yearFilter, searchQuery]);

  // Funktion för att växla sorteringsordningen
  const toggleSortOrder = () => {
    setSortOrder(prevOrder => (prevOrder === 'newest' ? 'oldest' : 'newest'));
  };

  // Funktion för att växla mellan grid och list vy
  const toggleViewMode = () => {
    setViewMode(prevMode => prevMode === 'grid' ? 'list' : 'grid');
  };


  return (
    <LogTheme>
      <Box padding={{ xs: 2, md: 4 }} sx={{ maxWidth: '1200px', margin: '0 auto' }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2} className="no-print">
          <Typography variant="h1">Tripploggar</Typography>
          <Button component={Link} to="/createlog" variant="contained" color="primary">
            Ny tripplogg
          </Button>
        </Box>

        {/* Print Header - only visible when printing */}
        <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
          <Typography variant="h1" gutterBottom>
            Mina loggar
          </Typography>
          {yearFilter && (
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Filtrerat på år: {yearFilter}
            </Typography>
          )}
          {searchQuery && (
            <Typography variant="body1" color="text.secondary" gutterBottom>
              Sökterm: {searchQuery}
            </Typography>
          )}
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            Totalt: {filteredLogs.length} logg{filteredLogs.length !== 1 ? 'ar' : ''}
          </Typography>
        </Box>

        {/* Filters and Search */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap" className="no-print">
          <TextField
            placeholder="Sök loggar..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            sx={{ flexGrow: 1, minWidth: '200px' }}
          />
          <FormControl sx={{ minWidth: 150 }}>
            <InputLabel>År</InputLabel>
            <Select
              value={yearFilter}
              label="År"
              onChange={(e) => setYearFilter(e.target.value)}
            >
              <MenuItem value="">Alla år</MenuItem>
              {availableYears.map(year => (
                <MenuItem key={year} value={year}>{year}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        {/* Controls */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2} className="no-print">
          <Box display="flex" gap={1} alignItems="center">
            {filteredLogs.length > 0 && (
              <Chip label={`${filteredLogs.length} logg${filteredLogs.length !== 1 ? 'ar' : ''}`} />
            )}
            {(yearFilter || searchQuery) && (
              <Button
                size="small"
                onClick={() => {
                  setYearFilter('');
                  setSearchQuery('');
                }}
                sx={{
                  backgroundColor: '#E3F2FD',
                  color: '#1565C0',
                  '&:hover': {
                    backgroundColor: '#BBDEFB',
                  },
                }}
              >
                Rensa filter
              </Button>
            )}
          </Box>
          <Box display="flex" gap={1}>
            <Tooltip title={viewMode === 'grid' ? 'Visa som lista' : 'Visa som rutnät'}>
              <IconButton 
                onClick={toggleViewMode} 
                color="primary"
                sx={{ border: '1px solid', borderColor: 'primary.main' }}
              >
                {viewMode === 'grid' ? <ViewListIcon /> : <ViewModuleIcon />}
              </IconButton>
            </Tooltip>
            <Button 
              onClick={toggleSortOrder} 
              sx={{
                backgroundColor: '#E3F2FD',
                color: '#1565C0',
                border: '1px solid #BBDEFB',
                '&:hover': {
                  backgroundColor: '#BBDEFB',
                  border: '1px solid #90CAF9',
                },
              }}
            >
              Sortera {sortOrder === 'newest' ? '↓' : '↑'}
            </Button>
          </Box>
        </Box>

        {/* Loading State */}
        {loading ? (
          <Grid container spacing={2}>
            {[1, 2, 3, 4].map((i) => (
              <Grid item xs={12} sm={6} md={4} lg={3} key={i}>
                <Card>
                  <CardContent>
                    <Skeleton variant="text" height={40} />
                    <Skeleton variant="text" height={20} />
                    <Skeleton variant="rectangular" height={36} sx={{ mt: 2 }} />
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        ) : filteredLogs.length === 0 ? (
          <Card>
            <CardContent>
              <Typography variant="h6" align="center" color="text.secondary">
                {logs.length === 0 ? 'Inga loggar hittades' : 'Inga loggar matchar dina filter'}
              </Typography>
            </CardContent>
          </Card>
        ) : (
          <>
            {/* Screen view - Grid */}
            {viewMode === 'grid' && (
              <Grid container spacing={2} className="no-print">
                {filteredLogs.map(log => (
                  <Grid item xs={12} sm={6} md={4} lg={3} key={log._id}>
                    <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s, box-shadow 0.2s', '&:hover': { transform: 'translateY(-4px)', boxShadow: 4 } }}>
                      <CardContent sx={{ flexGrow: 1, display: 'flex', flexDirection: 'column' }}>
                        {log.year && (
                          <Chip label={log.year} size="small" color="primary" sx={{ mb: 1, alignSelf: 'flex-start' }} />
                        )}
                        <Typography variant="h5" component="h2" gutterBottom sx={{ flexGrow: 1 }}>
                          {log.title}
                        </Typography>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                          {log.departureDateTime ? new Date(log.departureDateTime).toLocaleDateString('sv-SE') : '-'}
                        </Typography>
                        {log.position && (
                          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                            {log.position}
                          </Typography>
                        )}
                        <Button 
                          component={Link} 
                          to={`/showlog/${log._id}`} 
                          variant="contained" 
                          color="primary"
                          fullWidth
                        >
                          Visa
                        </Button>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}

            {/* Screen view - List */}
            {viewMode === 'list' && (
              <Box className="no-print">
                {filteredLogs.map(log => (
                  <Card key={log._id} sx={{ marginBottom: 2, transition: 'box-shadow 0.2s', '&:hover': { boxShadow: 4 } }}>
                    <CardContent>
                      <Box display="flex" justifyContent="space-between" alignItems="flex-start" flexWrap="wrap" gap={2}>
                        <Box flexGrow={1}>
                          {log.year && <Chip label={log.year} size="small" color="primary" sx={{ mb: 1 }} />}
                          <Typography variant="h4" gutterBottom>{log.title}</Typography>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            {log.departureDateTime ? new Date(log.departureDateTime).toLocaleDateString('sv-SE') : '-'}
                          </Typography>
                          {log.position && (
                            <Typography variant="body2" color="text.secondary" gutterBottom>
                              {log.position}
                            </Typography>
                          )}
                          {log.description && (
                            <Typography variant="body1" sx={{ mt: 1, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                              {log.description}
                            </Typography>
                          )}
                        </Box>
                        <Button component={Link} to={`/showlog/${log._id}`} variant="contained" color="primary">
                          Visa
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                ))}
              </Box>
            )}

            {/* Print view - always shows full details in list format */}
            <Box sx={{ display: 'none', '@media print': { display: 'block' } }}>
              {filteredLogs.map((log, index) => (
                <Card key={log._id} sx={{ marginBottom: 3, pageBreakInside: 'avoid' }}>
                  <CardContent>
                    <Box mb={2}>
                      {log.year && (
                        <Chip label={log.year} size="small" color="primary" sx={{ mb: 1 }} />
                      )}
                      <Typography variant="h3" gutterBottom>{log.title}</Typography>
                    </Box>
                    <Divider sx={{ my: 2 }} />
                    <Grid container spacing={2}>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Avgångstid
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {log.departureDateTime ? new Date(log.departureDateTime).toLocaleString('sv-SE') : '-'}
                        </Typography>
                      </Grid>
                      <Grid item xs={12} md={6}>
                        <Typography variant="body2" color="text.secondary" gutterBottom>
                          Ankomsttid
                        </Typography>
                        <Typography variant="body1" fontWeight={500}>
                          {log.arrivalDateTime ? new Date(log.arrivalDateTime).toLocaleString('sv-SE') : '-'}
                        </Typography>
                      </Grid>
                      {log.position && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Från – Till
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {log.position}
                          </Typography>
                        </Grid>
                      )}
                      {log.windDirection && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Vindriktning
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {log.windDirection}
                          </Typography>
                        </Grid>
                      )}
                      {log.windSpeed && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Vindstyrka (m/s)
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {log.windSpeed}
                          </Typography>
                        </Grid>
                      )}
                      {log.distance && (
                        <Grid item xs={12} md={6}>
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Distans (nm)
                          </Typography>
                          <Typography variant="body1" fontWeight={500}>
                            {log.distance}
                          </Typography>
                        </Grid>
                      )}
                      {log.description && (
                        <Grid item xs={12}>
                          <Divider sx={{ my: 2 }} />
                          <Typography variant="body2" color="text.secondary" gutterBottom>
                            Beskrivning
                          </Typography>
                          <Typography variant="body1" sx={{ whiteSpace: 'pre-wrap' }}>
                            {log.description}
                          </Typography>
                        </Grid>
                      )}
                    </Grid>
                    {(() => {
                      // Handle images - can be array or string that needs parsing
                      let images = log.images;
                      if (typeof images === 'string') {
                        try {
                          images = JSON.parse(images);
                        } catch (e) {
                          images = [];
                        }
                      }
                      return images && Array.isArray(images) && images.length > 0 && (
                        <>
                          <Divider sx={{ my: 3 }} />
                          <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
                            Bilder ({images.length})
                          </Typography>
                          <Grid container spacing={2}>
                            {images.map((imageUrl, imgIndex) => (
                                <Grid item xs={12} sm={6} md={4} key={imgIndex}>
                                  <Box
                                    component="img"
                                    src={getImageDisplayUrl(imageUrl)}
                                    alt={`Bild ${imgIndex + 1}`}
                                    sx={{
                                      width: '100%',
                                      height: 'auto',
                                      maxHeight: '300px',
                                      objectFit: 'contain',
                                      display: 'block',
                                    }}
                                  />
                                </Grid>
                            ))}
                          </Grid>
                        </>
                      );
                    })()}
                  </CardContent>
                </Card>
              ))}
            </Box>
          </>
        )}
      </Box>
    </LogTheme>
  );
}

export default Blog;